// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { isPlanLimitError } from '@/account/utilities/is-plan-limit-error';
import { ERROR_MESSAGES } from '@/api/constants';
import {
  hasProjectEndsBeforeItStartsError,
  hasProjectNameRequiredError,
  hasUniqueViolationError,
} from '@/api/error-utils';
import { FileInputButton } from '@/components/file-input-button';
import {
  DateRangeShortcut,
  FormDateRangePicker,
} from '@/components/form-date-range-picker';
import { FormTextField } from '@/components/form-text-field';
import { Link } from '@/components/link';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  DeleteRounded,
  UploadFileOutlined,
} from '@/constants/hardfin-standard-icons';
import { useToast } from '@/contexts/toast-context';
import { FormCustomerSearch } from '@/customer/components/form-customer-search';
import { getNewCompanyURL } from '@/customer/utilities/get-new-customer-url';
import { uploadFile } from '@/file/api/upload-file';
import { FormLocationSearch } from '@/location/components/form-location-search';
import { getNewLocationURL } from '@/location/utilities/get-new-location-url';
import {
  CreateProjectParameters,
  createProject,
} from '@/project/api/create-project';
import {
  PROJECT_NAME_MAX_LENGTH,
  PROJECT_NAME_MAX_LENGTH_MESSAGE,
  Project,
} from '@/project/api/types';
import { ProjectLocationFormWarning } from '@/project/components/project-location-form-warning';
import { ProjectRenewalCustomerWarning } from '@/project/components/project-renewal-customer-warning';
import { WorkflowSelector } from '@/project/components/workflow-selector';
import {
  SparseLocation,
  sparseCustomerSchema,
  sparseLocationSchema,
} from '@/sparse/api/types';
import { dateSchema } from '@/utilities/date-time-schema';
import { formatFileType } from '@/utilities/format-mime-type';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useId, useState } from 'react';
import {
  SubmitHandler,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';

const MAXIMUM_PRODUCT_COUNT = 3;
const NAME_REQUIRED_MESSAGE = 'Name is required';

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, NAME_REQUIRED_MESSAGE)
    .max(PROJECT_NAME_MAX_LENGTH, PROJECT_NAME_MAX_LENGTH_MESSAGE),
  customer: sparseCustomerSchema.nullable(),
  defaultLocation: sparseLocationSchema.nullable(),
  dateRange: z.tuple([dateSchema.nullable(), dateSchema.nullable()]).nullable(),
  inheritPreviousLineItems: z.boolean().default(false),
  workflowId: z.string().nullable().default(null),
});

type FormData = z.infer<typeof formSchema>;

function getDefaultValues(
  renewal: Project | undefined,
  initialName?: string
): FormData {
  if (renewal === undefined) {
    return {
      name: initialName ?? '',
      customer: null,
      defaultLocation: null,
      dateRange: null,
      inheritPreviousLineItems: false,
      workflowId: null,
    };
  }

  const values = {
    name: `${renewal.name} (renewal)`,
    customer: renewal.customer,
    defaultLocation: renewal.defaultLocation,
    dateRange: null,
    inheritPreviousLineItems: true, // Default for the checkbox is true
    workflowId: null,
  };

  if (renewal.startOn === null || renewal.endOn === null) {
    return values;
  }

  if (renewal.startOn > renewal.endOn) {
    return values;
  }

  const duration = renewal.endOn.diff(renewal.startOn, 'days');
  const start = renewal.endOn.plus({ days: 1 });
  const end = start.plus({ days: duration.days });

  return { ...values, dateRange: [start, end] };
}

interface Props {
  isOpen: boolean;
  renewal?: Project;
  onClose: () => void;
  /**
   * Optional initial name to pre-populate the project name field.
   * Useful when opening the dialog from a link with a "new" query parameter.
   */
  initialName?: string;
}

interface UploadedFile {
  id: string;
  key: string;
  name: string;
  type: string;
  size: number;
}

/**
 * Returns a human-readable string for a file size in bytes.
 * @param size - The file size in bytes.
 */
function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(0)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(0)} GB`;
}

export function CreateProjectDialog(props: Props) {
  const checkboxId = useId();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const { showToast } = useToast();

  const createProjectMutation = useMutation({
    mutationFn: (parameters: CreateProjectParameters) =>
      createProject(parameters),
    onSuccess: (response) => {
      close();
      queryClient.invalidateQueries({ queryKey: ['projects'] });

      showToast({
        severity: 'success',
        element: (
          <Typography>
            Project{' '}
            <Link
              to="/projects/$projectKey"
              params={{ projectKey: response.project.key }}
              color="primary.main"
              sx={{ fontWeight: 'bold', textDecoration: 'underline' }}
            >
              {response.project.name}
            </Link>{' '}
            {props.renewal ? 'was renewed' : 'created'}
          </Typography>
        ),
      });
    },
    onError: (error) => {
      if (hasUniqueViolationError(error)) {
        setError('name', {
          type: 'custom',
          message: 'A project with this name already exists',
        });
      }

      if (hasProjectNameRequiredError(error)) {
        setError('name', { type: 'custom', message: NAME_REQUIRED_MESSAGE });
      }

      if (hasProjectEndsBeforeItStartsError(error)) {
        setError('dateRange', {
          type: 'custom',
          message: ERROR_MESSAGES.project_ends_before_it_starts,
        });
      }
    },
  });

  /**
   * Removes a file from the uploadedFiles state by its id.
   * @param fileToDelete The file object to remove.
   */
  const handleDeleteFile = (fileToDelete: UploadedFile) => {
    setUploadedFiles((prevFiles) =>
      prevFiles.filter((file) => file.key !== fileToDelete.key)
    );
  };

  const uploadFileMutation = useMutation({
    /* 
    Note: the naming distinction is subtle here, but File is a native browser type,
    and FileType is a custom type defined in the File API.
    So we need to use File for the mutationFn parameter, but
    FileType is returned in the onSuccess parameter.
    */
    mutationFn: (file: File) => {
      setIsUploading(true);
      return uploadFile(file, {
        context: 'PROJECT_FILE',
        isPublic: false,
      });
    },
    onSuccess: (response, file) => {
      const newFile: UploadedFile = {
        id: response.fileId,
        key: response.fileKey,
        name: file.name,
        type: file.type,
        size: file.size,
      };
      setUploadedFiles((prev) => [...prev, newFile]);
      setIsUploading(false);
    },
    onError: (error) => {
      if (!isPlanLimitError(error)) {
        Sentry.captureException(error);
      }

      setIsUploading(false);
    },
  });

  const { control, reset, setError, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: getDefaultValues(props.renewal, props.initialName),
  });

  const inheritPreviousLineItemsController = useController({
    control,
    name: 'inheritPreviousLineItems',
  });
  const workflowController = useController({
    control,
    name: 'workflowId',
  });

  const name = useWatch({ control, name: 'name' });
  const customer = useWatch({ control, name: 'customer' });
  const defaultLocation = useWatch({ control, name: 'defaultLocation' });
  const dateRange = useWatch({ control, name: 'dateRange' });

  const isAlertOpen =
    createProjectMutation.isError &&
    !hasUniqueViolationError(createProjectMutation.error) &&
    !hasProjectNameRequiredError(createProjectMutation.error) &&
    !hasProjectEndsBeforeItStartsError(createProjectMutation.error) &&
    !isPlanLimitError(createProjectMutation.error);

  const dateRangeStart =
    dateRange === null || dateRange[0] === null
      ? DateTime.local().startOf('day')
      : dateRange[0];

  const productLabelColor = inheritPreviousLineItemsController.field.value
    ? 'text.primary'
    : 'text.secondary';

  const projectProducts = props.renewal?.items ?? [];

  const productsToRender =
    projectProducts.length > MAXIMUM_PRODUCT_COUNT
      ? projectProducts.slice(0, MAXIMUM_PRODUCT_COUNT - 1)
      : projectProducts;

  // Reset form when dialog opens with initialName
  useEffect(() => {
    if (props.isOpen) {
      reset(getDefaultValues(props.renewal, props.initialName));
    }
  }, [props.isOpen, props.initialName, props.renewal, reset]);

  const dateRangeShortcuts: DateRangeShortcut[] = [
    {
      label: '30 days',
      getValue: () => [dateRangeStart, dateRangeStart.plus({ days: 30 })],
    },
    {
      label: '90 days',
      getValue: () => [dateRangeStart, dateRangeStart.plus({ days: 90 })],
    },
    {
      label: '6 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 6 }).minus({ days: 1 }),
      ],
    },
    {
      label: '12 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 12 }).minus({ days: 1 }),
      ],
    },
    {
      label: '24 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 24 }).minus({ days: 1 }),
      ],
    },
    {
      label: '36 months',
      getValue: () => [
        dateRangeStart,
        dateRangeStart.plus({ months: 36 }).minus({ days: 1 }),
      ],
    },
  ];

  const handleChangeInheritPreviousLineItems = (
    _: unknown,
    isChecked: boolean
  ) => {
    inheritPreviousLineItemsController.field.onChange(isChecked);
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    const fileIds = uploadedFiles.map((file) => file.id);
    const parameters: CreateProjectParameters = {
      name: data.name,
      defaultLocationId: data.defaultLocation?.id,
      lineItems: [],
      inheritPreviousLineItems: data.inheritPreviousLineItems,
      fileIds: fileIds.length > 0 ? fileIds : undefined,
      workflowId: data.workflowId,
      workflowStageId: null,
    };

    if (data.customer) {
      parameters.customerId = data.customer.id;
    }

    if (data.dateRange) {
      if (data.dateRange[0]) {
        parameters.start = data.dateRange[0];
      }

      if (data.dateRange[1]) {
        parameters.end = data.dateRange[1];
      }
    }

    if (props.renewal === undefined) {
      return createProjectMutation.mutate(parameters);
    }

    return createProjectMutation.mutate({
      ...parameters,
      previousProjectId: props.renewal.id,
    });
  };

  const sortLocations = (location: SparseLocation, other: SparseLocation) => {
    const locationGroupName = location.customer?.name ?? 'Manufacturer';
    const otherGroupName = other.customer?.name ?? 'Manufacturer';

    if (locationGroupName === otherGroupName) {
      if (location.isArchived === other.isArchived) {
        return location.name.localeCompare(other.name);
      }

      return location.isArchived ? 1 : -1;
    }

    return locationGroupName.localeCompare(otherGroupName);
  };

  const handleCreateCustomer = (name: string) => {
    const url = getNewCompanyURL(name, true, false);

    window.open(url, '_blank');
  };

  const handleCreateLocation = (name: string) => {
    const url = getNewLocationURL(name);

    window.open(url, '_blank');
  };

  const close = () => {
    props.onClose();
    reset();
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <SupportSnackbar
          isOpen={isAlertOpen}
          onClose={createProjectMutation.reset}
          message="An error occurred while creating the project."
        />
        <DialogTitle>Add project</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {props.renewal && (
              <Stack useFlexGap paddingBottom={1}>
                <Typography variant="subtitle2">Previous project</Typography>
                <Typography variant="body2" marginRight="auto">
                  {props.renewal.name}
                </Typography>
              </Stack>
            )}
            <FormTextField control={control} name="name" label="Name" />
            <FormCustomerSearch
              name="customer"
              label="Customer (optional)"
              control={control}
              groupByMode="customer"
              onCreate={handleCreateCustomer}
            />
            <Stack>
              <FormLocationSearch
                name="defaultLocation"
                grouping={['customer']}
                label="Default location (optional)"
                sort={sortLocations}
                control={control}
                onCreate={handleCreateLocation}
              />
              <ProjectLocationFormWarning
                customer={customer}
                locations={defaultLocation ? [defaultLocation] : []}
                sx={{ marginTop: 2 }}
              />
            </Stack>
            <FormDateRangePicker
              name="dateRange"
              label="Date range (optional)"
              control={control}
              shortcuts={dateRangeShortcuts}
            />
            <WorkflowSelector
              value={workflowController.field.value}
              onChange={workflowController.field.onChange}
              disabled={createProjectMutation.isPending}
              label="Workflow (optional)"
            />
            {/* File Attachment Section */}
            <Stack spacing={1} paddingTop={1}>
              <Typography variant="subtitle2">
                Attachments (optional)
              </Typography>

              {/* This is a table of the files that have been uploaded. 
              It should be refactored to a separate component. */}
              {uploadedFiles.length > 0 && (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableBody>
                      {uploadedFiles.map((file) => (
                        <TableRow key={file.key}>
                          <TableCell sx={{ padding: 1.5 }}>
                            <Stack>
                              <Typography variant="body2">
                                {file.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {formatFileSize(file.size)} (
                                {formatFileType(file.name, file.type)})
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ padding: 1.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteFile(file)}
                              aria-label="Delete"
                            >
                              <DeleteRounded fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Show a skeleton row while uploading a file */}
                      {isUploading && (
                        <TableRow>
                          <TableCell sx={{ padding: 1.5 }}>
                            <Stack>
                              <Skeleton
                                variant="text"
                                width={120}
                                height={20}
                              />
                              <Skeleton variant="text" width={80} height={16} />
                            </Stack>
                          </TableCell>
                          <TableCell sx={{ padding: 1.5, paddingRight: 1 }}>
                            <Skeleton
                              variant="rounded"
                              width={20}
                              height={28}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <FileInputButton
                size="small"
                onChange={uploadFileMutation.mutate}
                disabled={isUploading}
                startIcon={<UploadFileOutlined fontSize="small" />}
                sx={{ alignSelf: 'start' }}
              >
                {/* Show a spinner while uploading a file */}
                Add file
                {isUploading && (
                  <CircularProgress size={16} sx={{ marginLeft: 1 }} />
                )}
              </FileInputButton>
            </Stack>
            {props.renewal && projectProducts.length > 0 && (
              <Box
                display="grid"
                gridTemplateColumns="min-content 1fr"
                alignItems="center"
              >
                <Checkbox
                  size="small"
                  name="inheritPreviousLineItems"
                  id={checkboxId}
                  checked={inheritPreviousLineItemsController.field.value}
                  onChange={handleChangeInheritPreviousLineItems}
                />
                <Typography
                  variant="body2"
                  component="label"
                  htmlFor={checkboxId}
                >
                  Carry over the same product details...
                </Typography>
                <ul
                  style={{
                    gridColumn: '2/2',
                    margin: 0,
                    padding: 0,
                    paddingLeft: theme.spacing(2),
                  }}
                >
                  {productsToRender.map((itemNode) => {
                    return (
                      <Typography
                        variant="body2"
                        key={itemNode.key}
                        color={productLabelColor}
                        component="li"
                        sx={{
                          marginTop: 1,
                          '&:first-child': {
                            marginTop: 0,
                          },
                        }}
                      >
                        {itemNode.quantity ?? 0} &times; {itemNode.item.name}
                      </Typography>
                    );
                  })}
                  {projectProducts.length > MAXIMUM_PRODUCT_COUNT && (
                    <Typography
                      variant="body2"
                      gridColumn="2/2"
                      color={productLabelColor}
                      fontStyle="italic"
                      component="li"
                      marginTop={1}
                    >
                      and {projectProducts.length - (MAXIMUM_PRODUCT_COUNT - 1)}{' '}
                      other products
                    </Typography>
                  )}
                </ul>
              </Box>
            )}
            {props.renewal && projectProducts.length == 0 && (
              <Typography color="text.secondary" variant="body2">
                The previous project includes no product details.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Stack
            useFlexGap
            flex={1}
            direction="row"
            alignItems="center"
            paddingLeft={1}
          >
            {props.renewal && (
              <ProjectRenewalCustomerWarning
                project={props.renewal}
                renewal={{
                  name: name === '' ? 'New project' : name,
                  customer: customer,
                }}
              />
            )}
            <Stack direction="row" marginLeft="auto" spacing={1}>
              <Button onClick={close}>Cancel</Button>
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isUploading || createProjectMutation.isPending}
              >
                Add
              </Button>
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>
    </>
  );
}
